import React, { useContext } from 'react';

import { getI18nData } from '@lucid/utils';

import Box from '../../box/box';
import Text from '../../text/text';
import LayoutContext from '../../context/layout-context';
import Link from '../../link/link';
import { type Lucid } from '../../../types/types';

export interface QuoteCarouselItemType {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The i18n translation data. */
  i18nData?: Lucid.I18n.DataEdges;
  /** List of quotes to include in the carousel */
  item: Lucid.Directus.Generated.Quote_Carousel_Item;
  /** Determines whether or not the quote is currently in view */
  isCurrentQuote?: boolean;
  /** Variant for links in quote */
  linkVariant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
  /** Typography variant for the quote text */
  quoteTextVariant?: Lucid.Theme.Component.Typography.Paragraph.VariantKeys;
}

const QuoteCarouselItem = ({
  className,
  item,
  i18nData,
  linkVariant,
  quoteTextVariant = 'paragraphXL300',
  isCurrentQuote,
  ...rest
}: QuoteCarouselItemType) => {
  const quoteLink = item.link;
  const quoteData = item.quote;
  const translatedData = quoteData?.translations?.[0];
  const attribution = `${quoteData?.full_name ? quoteData?.full_name : ''} ${
    translatedData?.position ? `— ${translatedData?.position}` : ''
  } ${quoteData?.company ? `at ${quoteData.company}` : ''}`;
  const quoteText = translatedData?.text;

  const { shortCode } = useContext(LayoutContext);
  const i18n = getI18nData(i18nData, shortCode);
  const urlArr = quoteLink?.final_url?.split('/') || undefined;
  const company = urlArr?.[urlArr?.indexOf('case-studies') + 1];

  return (
    quoteText && (
      <Box
        {...rest}
        css={{
          flex: '0 0 100%',
        }}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        <figure
          css={theme => ({
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
            maxWidth: '780px',
            margin: 'auto',
            padding: `0 ${theme.space.s48}px`,
            [theme.breakpoints.bp900.min]: {
              padding: `0 ${theme.space.s24}px`,
            },
          })}
        >
          <Text as="blockquote" css={{ marginInline: 0 }} variant={quoteTextVariant}>
            {quoteText}
          </Text>
          {attribution && (
            <figcaption>
              <Text
                css={theme => ({
                  fontWeight: theme.fontWeights.fw500,
                })}
                variant="paragraphM400"
              >
                {attribution}
              </Text>
            </figcaption>
          )}
          {quoteLink && quoteLink.final_url && (
            <Link
              aria-label={
                company ? i18n('mktg-ct-cs-study-link-aria-label', { company: company.replace('-', ' ') }) : ''
              }
              css={theme => ({
                marginBottom: theme.space.s16,
              })}
              href={quoteLink.final_url}
              iconAnimation="navigational"
              tabIndex={isCurrentQuote ? 0 : -1}
              variant={linkVariant}
            >
              {quoteLink.default_link_text}
            </Link>
          )}
        </figure>
      </Box>
    )
  );
};

export default QuoteCarouselItem;
